import React from 'react';

import { useAdLibrary, type UseAdLibrary } from '../../hooks/useAdLibrary';
import { fetchLandingPages, type LandingPage } from '../../landing-page-api';

/** The job kinds that write onto a landing page: its generation (hero, copy, game art landing op by op). Module-level
 *  so the hook's options stay stable. */
const LANDING_JOB_KINDS: readonly string[] = ['landing_page'];

export interface UseLandingPageLibrary extends UseAdLibrary<LandingPage> {
  /** The HTTP status of the last failed list read for this brand (0: unreachable), null when it was read. */
  listFailure: number | null;
}

/** A brand's saved landing pages, kept fresh while a generation lands (see {@link useAdLibrary}), and whether the
 *  list could be read (a 404: this environment does not serve saved landing pages yet). */
export const useLandingPageLibrary = (tenantId?: number): UseLandingPageLibrary => {
  const [failure, setFailure] = React.useState<{ tenantId: number; status: number } | null>(null);
  const library = useAdLibrary<LandingPage>(tenantId, {
    list: async (tenant, signal) => {
      const res = await fetchLandingPages(tenant, signal);
      if (!res.ok) {
        if (!res.aborted) setFailure({ tenantId: tenant, status: res.status });
        throw new Error(res.error || `landing pages unavailable (${res.status})`);
      }
      setFailure(null);
      return res.data;
    },
    doneKinds: LANDING_JOB_KINDS,
  });
  return { ...library, listFailure: failure && failure.tenantId === tenantId ? failure.status : null };
};

export default useLandingPageLibrary;
